import { takeLatest, call, fork, put, select } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { TemplateType } from '@dev/ee-api/ee-api-models';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { ConfigState, getConfigStore } from '@/store/config/types';

import i18n from '@/../i18n';
import { loadLandings } from '@/services/ApiCategories/landings';
import { errorToast } from '@/sagas/commonGenerators/errorToast';
import { LandingPagePaths } from '../services/dictionaries/Paths';
import {
    LoadingTemplatesSuccess,
    LoadingTemplatesError,
    LoadingAlreadySubscribedLandingPagesError,
    LoadingAlreadySubscribedLandingPagesSuccess,
    LoadingSubmitLandingPagesError,
    LoadingSubmitLandingPagesSuccess,
    templateDeleteSuccess,
    templateDeleteError,
    templateUpdateSuccess,
    templateUpdateError,
    templatePreviewSuccess,
    templatePreviewError,
    AddTemplateSuccess,
    AddTemplateError,
    templateArrayDeleteSuccess,
    templateArrayDeleteError,
    downloadLandingPageSuccess,
    downloadLandingPageError,
    getLandingPageList,
    LoadingTemplateSuccess,
    LoadingTemplateError,
    LoadingTemplateSubmitSuccess,
    LoadingTemplateSubmitError,
    LoadingTemplateAlreadySubscribedSuccess,
    LoadingTemplateAlreadySubscribedError,
    LoadLandingPagesListSuccess,
    LoadLandingPagesListError
} from '../store/landingPageList/actions';
import {
    LoadListStartAction,
    LoadAlreadySubscribedLandingPagesListStartAction,
    LoadSubmitLandingPagesStartAction,
    LandingPagesActionTypes,
    templateDeleteStartAction,
    templatePreviewStartAction,
    templateArrayDeleteStartAction,
    downloadLandingPageStartAction,
    LoadTemplateStart,
    PageTypeEnum,
    LoadLandingPagesListStartAction,
} from '../store/landingPageList/types';
import {
    loadTemplateList,
    deleteTemplate,
    deleteTemplateArray,
    previewTemplate,
    updateTemplate,
    copyTemplate,
    downloadTemplate,
    loadTemplate
} from '../services/ApiCategories/templates';
import { ShowToast, HideToast } from '../store/toast/actions';

let templatesListTemp;
function* fetchTemplatesList(action: LoadListStartAction): any {
    const { payload } = action;
    const offset = payload.page === 1 ? 0 : (payload.page - 1) * payload.limit;
    const params = {
        type: payload.type,
        offset,
        limit: payload.limit,
        search: payload.search,
        templateOrder: payload.templateOrder,
        templateTypes: payload.templateTypes,
    };

    try {
        const list = yield call(loadTemplateList, params);
        templatesListTemp = list;
        const { templatesList } = yield select(getLandingPageList);
        const previousTemplatesList = offset === 0 ? [] : templatesList;
        const templates = [...list.Templates, ...list.DraftTemplate, ...list.GlobalTemplate, ...list.ParentTemplates];
        const count =
            list.TemplatesCount + list.DraftTemplatesCount + list.GlobalTemplatesCount + list.ParentTemplatesCount;

        const hasMore = templates.length === payload.limit;
        yield put(LoadingTemplatesSuccess(payload.isInfinityScroll ? [...previousTemplatesList, ...templates] : templates, count, hasMore));
    } catch (e) {
        yield call(errorToast, e);
        yield put(LoadingTemplatesError());
    }
}
function* fetchFormLandingPagesList(action: LoadSubmitLandingPagesStartAction | LoadAlreadySubscribedLandingPagesListStartAction): any {
    const { payload } = action;
    const offset = payload.page === 1 ? 0 : (payload.page - 1) * payload.limit;
    const params = {
        type: payload.type,
        offset,
        limit: payload.limit,
        search: payload.search,
        templateOrder: payload.templateOrder,
        templateTypes: payload.templateTypes,
    };

    try {
        const list = yield call(loadTemplateList, params);
        const { submitLandingPagesList, alreadySubscribedLandingPagesList } = yield select(getLandingPageList);
        const previousPageList = payload.pageType === PageTypeEnum.FormSubmit ? submitLandingPagesList : alreadySubscribedLandingPagesList;
        const previousTemplatesList = offset === 0 ? [] : previousPageList;
        const templates = [...list.Templates, ...list.DraftTemplate, ...list.GlobalTemplate, ...list.ParentTemplates];
        const hasMore = templates.length === payload.limit;

        if (payload.pageType === PageTypeEnum.FormSubmit) {
            yield put(LoadingSubmitLandingPagesSuccess(payload.isInfinityScroll ? [...previousTemplatesList, ...templates] : templates, hasMore));
        } else {
            yield put(LoadingAlreadySubscribedLandingPagesSuccess(payload.isInfinityScroll ? [...previousTemplatesList, ...templates] : templates, hasMore));
        }

    } catch (e) {
        yield call(errorToast, e);

        if (payload.pageType === PageTypeEnum.FormSubmit) {
            yield put(LoadingSubmitLandingPagesError());
        } else {
            yield put(LoadingAlreadySubscribedLandingPagesError());
        }
    }
}
function* watchFetchSubmitLandingPagesList(): any {
    yield takeLatest(LandingPagesActionTypes.LOADING_SUBMIT_LANDING_PAGES_START, fetchFormLandingPagesList);
}
function* watchFetchAlreadySubscribedLandingPagesList(): any {
    yield takeLatest(LandingPagesActionTypes.LOADING_ALREADY_SUBSCRIBED_LANDING_PAGES_START, fetchFormLandingPagesList);
}

function* fetchTemplate(action: LoadTemplateStart): any {
    const { payload } = action;

    try {
        const template = yield call(loadTemplate, payload.name, false);
        
        switch (payload.pageType) {
            case PageTypeEnum.DoubleOptIn:
                yield put(LoadingTemplateSuccess(template));
                break;
            case PageTypeEnum.FormSubmit:
                yield put(LoadingTemplateSubmitSuccess(template));
                break;
            case PageTypeEnum.AlreadySubscribed:
                yield put(LoadingTemplateAlreadySubscribedSuccess(template));
                break;
            default:
                break;
        }
    } catch (e) {
        yield call(errorToast, e);

        switch (payload.pageType) {
            case PageTypeEnum.DoubleOptIn:
                yield put(LoadingTemplateError());
                break;
            case PageTypeEnum.FormSubmit:
                yield put(LoadingTemplateSubmitError());
                break;
            case PageTypeEnum.AlreadySubscribed:
                yield put(LoadingTemplateAlreadySubscribedError());
                break;
            default:
                break;
        }
    }
}
function* watchFetchTemplate(): any {
    yield takeLatest(LandingPagesActionTypes.LOADING_LANDING_PAGE_START, fetchTemplate);
}
function* watchFetchSubmitTemplate(): any {
    yield takeLatest(LandingPagesActionTypes.LOADING_LANDING_PAGE_SUBMIT_START, fetchTemplate);
}
function* watchFetchTemplateAlreadySubscribed(): any {
    yield takeLatest(LandingPagesActionTypes.LOADING_LANDING_PAGE_ALREADY_SUBSCRIBED_START, fetchTemplate);
}
function* deleteSingleTemplate(action: templateDeleteStartAction): any {
    const { payload } = action;

    const offset = payload.page === 1 ? 0 : (payload.page - 1) * payload.limit;
    const params = {
        type: payload.type,
        offset,
        limit: payload.limit,
        search: payload.search,
        templateOrder: payload.templateOrder,
        templateTypes: payload.templateTypes,
        page: payload.page,
    };
    try {
        yield call(deleteTemplate, payload.name);

        const count = templatesListTemp.TemplatesCount - 1;
        yield put(templateDeleteSuccess(payload.name, templatesListTemp.Templates, count, params));
        yield put(
            ShowToast({
                message: i18n.t('landingpagedeleted'),
                severity: 'info',
                duration: 3000,
                position: 'bottom-left',
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(templateDeleteError());
        yield call(errorToast, e);
    }
}
function* deleteArrayOfTemplates(action: templateArrayDeleteStartAction): any {
    const { payload } = action;

    const offset = payload.page === 1 ? 0 : (payload.page - 1) * payload.limit;
    const params = {
        type: payload.type,
        offset,
        limit: payload.limit,
        search: payload.search,
        templateOrder: payload.templateOrder,
        templateTypes: payload.templateTypes,
        page: payload.page,
    };
    try {
        yield call(deleteTemplateArray, payload.templatesToDelete);
        const count = templatesListTemp.TemplatesCount - payload.templatesToDelete.length;
        yield put(templateArrayDeleteSuccess(templatesListTemp.Templates, count, params));
        yield put(
            ShowToast({
                message: i18n.t('landingpagesdeleted'),
                severity: 'info',
                duration: 3000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(templateArrayDeleteError());
        yield call(errorToast, e);
    }
}
let updatedTemplateList;
function* updateTemplateFunc(action): any {
    const { payload } = action;

    try {
        yield call(updateTemplate, payload.name, payload.template, payload.removeScreenshot);
        if (templatesListTemp) {
            let currentList = templatesListTemp?.Templates;
            if (updatedTemplateList) {
                currentList = updatedTemplateList;
            }

            updatedTemplateList = currentList.map((template) => {
                if (template.Name === payload.template.Name) {
                    //replace with new value
                    return payload.template;
                }
                if (payload.template.IsDefault && payload.template.TemplateType === template.TemplateType) {
                    //if selecting new default, mark previous not default
                    return {
                        ...template,
                        IsDefault: false,
                    };
                }

                return template;
            });
        }
        yield put(templateUpdateSuccess(payload.name, updatedTemplateList));
    } catch (e) {
        yield put(templateUpdateError());
        yield call(errorToast, e);
    }
}
function* previewTemplateFunc(action: templatePreviewStartAction): any {
    const { payload } = action;

    try {
        const name = safelyDecodeBase64Url(payload.name);

        const template = yield call(previewTemplate, name, payload.ispublic);
        yield put(templatePreviewSuccess(name, template, payload.ispublic));
    } catch (e) {
        yield put(templatePreviewError());

        yield call(errorToast, e);
    }
}
function* downloadLandingPage(action: downloadLandingPageStartAction): any {
    const { payload } = action;

    try {
        const name = safelyDecodeBase64Url(payload.name);
        const landingPageToDownload = yield call(downloadTemplate, name);
        const blob = new Blob([landingPageToDownload.data]);
        const templatename = payload.name;
        const fileName = `${templatename}.html`;
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();

        yield put(downloadLandingPageSuccess(payload.name));
    } catch (e) {
        yield put(downloadLandingPageError());
        yield call(errorToast, e);
    }
}
function* addTemplate(action): any {
    const { payload } = action;
    const { isStandalone }: ConfigState = yield select(getConfigStore);

    try {
        const templates = templatesListTemp.Templates;
        const template = yield call(copyTemplate, payload.prevTemplateName, payload.changes);

        const count = templatesListTemp.TemplatesCount + 1;

        yield put(AddTemplateSuccess(payload.template, templates, count));
        if (template.TemplateType === TemplateType.LandingPageEditor) {
            const editorPath = isStandalone ? LandingPagePaths.LegacyDragDropEditor : LandingPagePaths.DragDropEditor;
            yield put(push(`${editorPath}/${safelyEncodeBase64Url(template.Name)}`));
        } else {
            yield put(push(`${LandingPagePaths.EditorLink}/${safelyEncodeBase64Url(template.Name)}`));
        }
        yield put(
            ShowToast({
                message: 'Landing Page copied',
                severity: 'info',
                duration: 3000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(AddTemplateError());
        yield call(errorToast, e);
    }
}
function* loadLandingPagesList(action: LoadLandingPagesListStartAction): any {
    const { payload } = action;
    const { offset, limit, type } = payload;

    try {
        const list = yield call(loadLandings, type, limit, offset);

        yield put(LoadLandingPagesListSuccess(list));
    } catch (e) {
        yield put(LoadLandingPagesListError());
        yield call(errorToast, e);
    }
}

function* watchDownloadLandingPage(): any {
    yield takeLatest('@@landingPageList/DOWNLOAD_LANDING_PAGE_START', downloadLandingPage);
}
function* watchAddTemplate(): any {
    yield takeLatest('@@landingPageList/SAVE_TEMPLATE_START', addTemplate);
}
function* watchFetchTemplateList(): any {
    yield takeLatest('@@landingPageList/LOADING_START', fetchTemplatesList);
}
function* watchdeleteSingleTemplate(): any {
    yield takeLatest('@@landingPageList/DELETE_START', deleteSingleTemplate);
}
function* watchdeleteSingleTemplateSuccess(): any {
    yield takeLatest('@@landingPageList/DELETE_SUCCESS', fetchTemplatesList);
}
function* watchdeleteArrayOfTemplates(): any {
    yield takeLatest('@@landingPageList/DELETE_ARRAY_START', deleteArrayOfTemplates);
}
function* watchdeleteArrayOfTemplatesSuccess(): any {
    yield takeLatest('@@landingPageList/DELETE_ARRAY_SUCCESS', fetchTemplatesList);
}
function* watchPreviewTemplate(): any {
    yield takeLatest('@@landingPageList/PREVIEW_START', previewTemplateFunc);
}
function* watchUpdateTemplate(): any {
    yield takeLatest('@@landingPageList/UPDATE_START', updateTemplateFunc);
}
function* watchLoadLandingPagesList(): any {
    yield takeLatest('@@landingPageList/LOAD_LANDING_PAGES_LIST_START', loadLandingPagesList);
}

const landingPageListSagas = [
    fork(watchFetchTemplateList),
    fork(watchdeleteSingleTemplate),
    fork(watchdeleteArrayOfTemplates),
    fork(watchPreviewTemplate),
    fork(watchUpdateTemplate),
    fork(watchAddTemplate),
    fork(watchdeleteSingleTemplateSuccess),
    fork(watchdeleteArrayOfTemplatesSuccess),
    fork(watchDownloadLandingPage),
    fork(watchFetchSubmitLandingPagesList),
    fork(watchFetchAlreadySubscribedLandingPagesList),
    fork(watchFetchTemplate),
    fork(watchFetchSubmitTemplate),
    fork(watchFetchTemplateAlreadySubscribed),
    fork(watchLoadLandingPagesList)
];

export default landingPageListSagas;
